import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, Eye, Globe, Loader2, UserCircle, Building2, Users, Package, Scale, AlertTriangle } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { PhoneInput } from '../components/ui/phone-input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import { Select } from '../components/ui/select'
import { Switch } from '../components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { CountrySelect } from '../components/ui/country-select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { useCreateInvoice, useNextInvoiceNumber } from '../hooks/useInvoices'
import { useSenderProfiles, useBankAccounts, useUserPreferences } from '../hooks/useSettings'
import { useClients } from '../hooks/useClients'
import { useProducts, useSearchProducts } from '../hooks/useProducts'
import { toast } from '../hooks/useToast'
import { InvoiceLanguage } from '../services/pdfGenerator'
import { getApiError } from '../lib/api'
import {
  InvoiceCreate as InvoiceCreateType,
  InvoiceItemForm,
  InvoiceSender,
  InvoiceRecipient,
  PaymentDetails,
  VAT_RATES,
  WITHHOLDING_RATES,
  VAT_EXEMPTION_REASONS,
} from '../types/invoice'
import { Product } from '../types/product'

const generateId = () => Math.random().toString(36).substring(2, 11)

const languages: { value: InvoiceLanguage; label: string; flag: string }[] = [
  { value: 'es', label: 'Español', flag: '🇪🇸' },
  { value: 'en', label: 'English', flag: '🇬🇧' },
  { value: 'ru', label: 'Русский', flag: '🇷🇺' },
  { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { value: 'fr', label: 'Français', flag: '🇫🇷' },
]

const defaultPaymentDetails: PaymentDetails = {
  iban: '',
  swift: '',
  bankName: '',
  accountHolder: '',
  reference: '',
}

const defaultSender: InvoiceSender = {
  name: '',
  nif: '',
  street: '',
  city: '',
  postalCode: '',
  countryCode: '',
  email: '',
  phone: '',
  paymentDetails: defaultPaymentDetails,
}

const defaultRecipient: InvoiceRecipient = {
  name: '',
  nif: '',
  street: '',
  city: '',
  postalCode: '',
  countryCode: '',
}

const createEmptyItem = (): InvoiceItemForm => ({
  id: generateId(),
  description: '',
  quantity: 1,
  priceWithoutVat: 0,
  vatRate: 21,
  withholdingRate: 0,
  subtotalWithoutVat: 0,
  subtotalWithVat: 0,
})

export default function InvoiceCreate() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const createInvoiceMutation = useCreateInvoice()

  // Fetch saved profiles, accounts, preferences, clients and products
  const { data: senderProfiles = [] } = useSenderProfiles()
  const { data: bankAccounts = [] } = useBankAccounts()
  const { data: userPreferences } = useUserPreferences()
  const { data: clients = [] } = useClients()
  const { data: products = [] } = useProducts(true) // Only active products

  // Get invoice prefix from user preferences
  const invoicePrefix = userPreferences?.invoicePrefix || ''
  const { getNextInvoiceNumber } = useNextInvoiceNumber(invoicePrefix)

  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0])
  const [sender, setSender] = useState<InvoiceSender>(defaultSender)
  const [recipient, setRecipient] = useState<InvoiceRecipient>(defaultRecipient)
  const [items, setItems] = useState<InvoiceItemForm[]>([createEmptyItem()])
  const [vatExemptionReason, setVatExemptionReason] = useState('')
  const [notes, setNotes] = useState('')
  const [includePaymentDetails, setIncludePaymentDetails] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const vatExemptionRef = useRef<HTMLDivElement>(null)

  // Selection state for profiles, accounts and clients
  const [selectedProfileId, setSelectedProfileId] = useState<string>('')
  const [selectedAccountId, setSelectedAccountId] = useState<string>('')
  const [selectedClientId, setSelectedClientId] = useState<string>('')
  const [showProductDialog, setShowProductDialog] = useState(false)

  // Autocomplete state
  const [autocompleteItemId, setAutocompleteItemId] = useState<string | null>(null)
  const [autocompleteQuery, setAutocompleteQuery] = useState('')
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { data: searchResults = [] } = useSearchProducts(autocompleteQuery)

  // Update invoice number when preferences load
  useEffect(() => {
    setInvoiceNumber(getNextInvoiceNumber())
  }, [getNextInvoiceNumber, invoicePrefix])

  // Handle duplicate invoice dataY
  useEffect(() => {
    const isDuplicate = searchParams.get('duplicate') === 'true'
    if (isDuplicate) {
      const duplicateData = sessionStorage.getItem('duplicateInvoice')
      if (duplicateData) {
        try {
          const data = JSON.parse(duplicateData)
          if (data.sender) setSender(data.sender)
          if (data.recipient) setRecipient(data.recipient)
          if (data.items && data.items.length > 0) {
            setItems(data.items.map((item: InvoiceItemForm) => ({
              ...item,
              id: generateId(),
              subtotalWithoutVat: item.quantity * item.priceWithoutVat,
              subtotalWithVat: item.quantity * item.priceWithoutVat * (1 + item.vatRate / 100),
            })))
          }
          if (data.vatExemptionReason) setVatExemptionReason(data.vatExemptionReason)
          if (data.notes) setNotes(data.notes)
          // Enable payment details if duplicate has them
          if (data.sender?.paymentDetails?.iban) setIncludePaymentDetails(true)
          // Clear the duplicate data
          sessionStorage.removeItem('duplicateInvoice')
          // Set manual mode for profiles since we're using duplicated data
          setSelectedProfileId('manual')
          setSelectedAccountId('manual')
        } catch {
          // Ignore parsing errors
        }
      }
    }
  }, [searchParams])
  
  const handleSelectProfile = useCallback((profileId: string) => {
    setSelectedProfileId(profileId)
    if (profileId === 'manual') {
      setSender(defaultSender)
      return
    }
    const profile = senderProfiles.find(p => String(p.id) === profileId)
    if (profile) {
      setSender(prev => ({
        ...prev,
        name: profile.companyName,
        nif: profile.taxId || '',
        street: profile.street || '',
        city: profile.city || '',
        postalCode: profile.postalCode || '',
        countryCode: profile.countryCode || '',
        email: profile.email || '',
        phone: profile.phone || '',
      }))
    }
  }, [senderProfiles])

  const handleSelectClient = useCallback((clientId: string) => {
    setSelectedClientId(clientId)
    if (clientId === 'manual' || clientId === '') {
      setRecipient(defaultRecipient)
      return
    }
    const client = clients.find(c => String(c.id) === clientId)
    if (client) {
      setRecipient({
        name: client.name,
        nif: client.taxId || '',
        street: client.street || '',
        city: client.city || '',
        postalCode: client.postalCode || '',
        countryCode: client.countryCode || '',
      })
    }
  }, [clients])
  
  const handleSelectAccount = useCallback((accountId: string) => {
    setSelectedAccountId(accountId)
    if (accountId === 'manual') {
      setSender(prev => ({
        ...prev,
        paymentDetails: defaultPaymentDetails,
      }))
      setIncludePaymentDetails(true)
      return
    }
    const account = bankAccounts.find(a => String(a.id) === accountId)
    if (account) {
      setSender(prev => ({
        ...prev,
        paymentDetails: {
          ...(prev.paymentDetails || defaultPaymentDetails),
          iban: account.iban || '',
          swift: account.swiftBic || '',
          bankName: account.bankName,
          accountHolder: prev.name || '',
        },
      }))
      setIncludePaymentDetails(true)
    }
  }, [bankAccounts])

  // Auto-select default profile
  useEffect(() => {
    const defaultProfile = senderProfiles.find(p => p.isDefault)
    if (defaultProfile && !selectedProfileId) {
      handleSelectProfile(String(defaultProfile.id))
    }
  }, [senderProfiles, selectedProfileId, handleSelectProfile])

  // Auto-select default bank account
  useEffect(() => {
    const defaultAccount = bankAccounts.find(a => a.isDefault)
    if (defaultAccount && !selectedAccountId) {
      handleSelectAccount(String(defaultAccount.id))
    }
  }, [bankAccounts, selectedAccountId, handleSelectAccount])

  // Auto-select default client
  useEffect(() => {
    const defaultClient = clients.find(c => c.isDefault)
    if (defaultClient && !selectedClientId) {
      handleSelectClient(String(defaultClient.id))
    }
  }, [clients, selectedClientId, handleSelectClient])

  const addProductToInvoice = (product: Product) => {
    const newItem: InvoiceItemForm = {
      id: generateId(),
      description: product.description || product.name,
      quantity: 1,
      priceWithoutVat: product.price,
      vatRate: product.vatRate,
      withholdingRate: product.withholdingRate,
      subtotalWithoutVat: product.price,
      subtotalWithVat: product.price * (1 + product.vatRate / 100),
    }
    setItems(prev => [...prev, newItem])
    setShowProductDialog(false)
  }

  const updatePaymentDetails = (field: keyof PaymentDetails, value: string) => {
    setSender(prev => ({
      ...prev,
      paymentDetails: {
        ...(prev.paymentDetails || defaultPaymentDetails),
        [field]: value,
      },
    }))
  }

  const updateItem = (id: string, field: keyof InvoiceItemForm, value: string | number) => {
    setItems(prev =>
      prev.map(item => {
        if (item.id !== id) return item

        const updated = { ...item, [field]: value }

        const baseAmount = updated.quantity * updated.priceWithoutVat
        const vatAmount = baseAmount * (updated.vatRate / 100)
        updated.subtotalWithoutVat = baseAmount
        updated.subtotalWithVat = baseAmount + vatAmount

        return updated
      })
    )
  }

  const addItem = () => {
    setItems(prev => [...prev, createEmptyItem()])
  }

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== id))
    }
  }

  const calculateTotals = () => {
    const baseAmount = items.reduce((sum, item) => sum + item.subtotalWithoutVat, 0)
    const totalVat = items.reduce(
      (sum, item) => sum + item.subtotalWithoutVat * (item.vatRate / 100),
      0
    )
    const totalWithholding = items.reduce(
      (sum, item) => sum + item.subtotalWithoutVat * (item.withholdingRate / 100),
      0
    )
    const totalAmount = baseAmount + totalVat - totalWithholding

    return { baseAmount, totalVat, totalWithholding, totalAmount }
  }

  const totals = calculateTotals()

  // Check if any item has 0% VAT — requires exemption reason
  const hasZeroVatItem = items.some(item => item.vatRate === 0 && item.priceWithoutVat > 0)
  const needsExemptionReason = hasZeroVatItem && !vatExemptionReason
  const prevHasZeroVatRef = useRef(false)

  // Auto-scroll to VAT exemption card when 0% VAT item appears
  useEffect(() => {
    if (hasZeroVatItem && !prevHasZeroVatRef.current) {
      setTimeout(() => {
        vatExemptionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }, 100)
    }
    prevHasZeroVatRef.current = hasZeroVatItem
  }, [hasZeroVatItem])

  const validateBeforeSave = (): boolean => {
    if (needsExemptionReason) {
      toast({
        variant: 'destructive',
        title: t('invoices.form.vatExemptionRequired'),
        description: t('invoices.form.vatExemptionRequiredDesc'),
      })
      return false
    }
    return true
  }

  const createInvoiceData = (): InvoiceCreateType => {
    const senderData: InvoiceSender = includePaymentDetails
      ? sender
      : { ...sender, paymentDetails: undefined }
    return {
      invoiceNumber: invoiceNumber.trim() || undefined,
      date: new Date(invoiceDate).toISOString(),
      sender: senderData,
      recipient,
      items: items.map(item => ({
        description: item.description,
        quantity: item.quantity,
        priceWithoutVat: item.priceWithoutVat,
        vatRate: item.vatRate,
        withholdingRate: item.withholdingRate,
      })),
      vatExemptionReason: vatExemptionReason || undefined,
      notes: notes.trim() || undefined,
      // Include IDs to link with saved entities (backend will auto-save new ones)
      clientId: selectedClientId && selectedClientId !== 'manual' ? Number(selectedClientId) : undefined,
      senderProfileId: selectedProfileId && selectedProfileId !== 'manual' ? Number(selectedProfileId) : undefined,
    }
  }

  const handleSaveDraft = async () => {
    if (!validateBeforeSave()) return
    setIsSaving(true)
    try {
      const savedInvoice = await createInvoiceMutation.mutateAsync(createInvoiceData())
      toast({
        title: t('invoices.toast.saved'),
        description: t('invoices.toast.savedDesc'),
      })
      navigate(`/dashboard/invoices/${savedInvoice.id}`)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('invoices.toast.errorSaving'),
        description: apiError.message,
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAndPreview = async (language: InvoiceLanguage) => {
    if (!validateBeforeSave()) return
    setIsSaving(true)
    try {
      const savedInvoice = await createInvoiceMutation.mutateAsync(createInvoiceData())

      toast({
        title: t('invoices.toast.created'),
        description: t('invoices.toast.createdDesc'),
      })
      // Navigate to detail page with preview auto-open
      navigate(`/dashboard/invoices/${savedInvoice.id}?preview=${language}`)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('invoices.toast.errorSaving'),
        description: apiError.message,
      })
    } finally {
      setIsSaving(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  const isSubmitting = isSaving || createInvoiceMutation.isPending

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('invoices.createInvoice')}</h1>
          <p className="text-muted-foreground">
            {t('invoices.createSubtitle')}
          </p>
        </div>
      </div>

      {/* Invoice Details */}
      <Card>
        <CardHeader>
          <CardTitle>{t('invoices.form.details')}</CardTitle>
          <CardDescription>{t('invoices.form.detailsDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="invoiceNumber">{t('invoices.form.invoiceNumber')}</Label>
              <Input
                id="invoiceNumber"
                value={invoiceNumber}
                onChange={e => setInvoiceNumber(e.target.value)}
                placeholder="2024-0001"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoiceDate">{t('invoices.form.invoiceDate')}</Label>
              <Input
                id="invoiceDate"
                type="date"
                value={invoiceDate}
                onChange={e => setInvoiceDate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Sender Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t('invoices.form.sender')}</CardTitle>
            <CardDescription>{t('invoices.form.senderDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Profile Selector */}
            {senderProfiles.length > 0 && (
              <div className="space-y-2 pb-4 border-b">
                <Label className="flex items-center gap-2">
                  <UserCircle className="h-4 w-4" />
                  {t('invoices.form.selectProfile')}
                </Label>
                <Select
                  value={selectedProfileId}
                  onChange={(e) => handleSelectProfile(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">{t('invoices.form.selectProfilePlaceholder')}</option>
                  {senderProfiles.map(profile => (
                    <option key={profile.id} value={String(profile.id)}>
                      {profile.name} {profile.isDefault ? `(${t('settings.default')})` : ''}
                    </option>
                  ))}
                  <option value="manual">{t('invoices.form.manualEntry')}</option>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="senderName">{t('invoices.form.nameOrCompany')}</Label>
              <Input
                id="senderName"
                value={sender.name}
                onChange={e => setSender({ ...sender, name: e.target.value })}
                placeholder={t('invoices.form.placeholders.senderName')}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="senderNif">{t('invoices.form.nif')}</Label>
                <Input
                  id="senderNif"
                  value={sender.nif}
                  onChange={e => setSender({ ...sender, nif: e.target.value })}
                  placeholder={t('invoices.form.placeholders.nif')}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senderPhone">{t('invoices.form.phone')}</Label>
                <PhoneInput
                  id="senderPhone"
                  value={sender.phone}
                  onChange={(val) => setSender({ ...sender, phone: val })}
                  placeholder={t('invoices.form.placeholders.phone')}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="senderStreet">{t('invoices.form.street')}</Label>
              <Input
                id="senderStreet"
                value={sender.street || ''}
                onChange={e => setSender({ ...sender, street: e.target.value })}
                placeholder={t('invoices.form.placeholders.street')}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="senderCity">{t('invoices.form.city')}</Label>
                <Input
                  id="senderCity"
                  value={sender.city || ''}
                  onChange={e => setSender({ ...sender, city: e.target.value })}
                  placeholder={t('invoices.form.placeholders.city')}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senderPostalCode">{t('invoices.form.postalCode')}</Label>
                <Input
                  id="senderPostalCode"
                  value={sender.postalCode || ''}
                  onChange={e => setSender({ ...sender, postalCode: e.target.value })}
                  placeholder={t('invoices.form.placeholders.postalCode')}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senderCountry">{t('invoices.form.country')}</Label>
                <CountrySelect
                  id="senderCountry"
                  value={sender.countryCode || ''}
                  onChange={(value) => setSender({ ...sender, countryCode: value })}
                  disabled={isSubmitting}
                  placeholder={t('invoices.form.selectCountry')}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="senderEmail">{t('invoices.form.email')}</Label>
              <Input
                id="senderEmail"
                type="email"
                value={sender.email}
                onChange={e => setSender({ ...sender, email: e.target.value })}
                placeholder={t('auth.placeholders.email')}
                disabled={isSubmitting}
              />
            </div>
          </CardContent>
        </Card>

        {/* Recipient Info */}
        <Card>
          <CardHeader>
            <CardTitle>{t('invoices.form.recipient')}</CardTitle>
            <CardDescription>{t('invoices.form.recipientDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Client Selector */}
            {clients.length > 0 && (
              <div className="space-y-2 pb-4 border-b">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  {t('invoices.form.selectClient')}
                </Label>
                <Select
                  value={selectedClientId}
                  onChange={(e) => handleSelectClient(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">{t('invoices.form.selectClientPlaceholder')}</option>
                  {clients.map(client => (
                    <option key={client.id} value={String(client.id)}>
                      {client.name} {client.taxId ? `(${client.taxId})` : ''} {client.isDefault ? `- ${t('settings.default')}` : ''}
                    </option>
                  ))}
                  <option value="manual">{t('invoices.form.manualEntry')}</option>
                </Select>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="recipientName">{t('invoices.form.nameOrCompany')}</Label>
                <Input
                  id="recipientName"
                  value={recipient.name}
                  onChange={e => setRecipient({ ...recipient, name: e.target.value })}
                  placeholder={t('invoices.form.placeholders.recipientName')}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipientNif">{t('invoices.form.nif')}</Label>
                <Input
                  id="recipientNif"
                  value={recipient.nif}
                  onChange={e => setRecipient({ ...recipient, nif: e.target.value })}
                  placeholder={t('invoices.form.placeholders.nif')}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="recipientStreet">{t('invoices.form.street')}</Label>
              <Input
                id="recipientStreet"
                value={recipient.street || ''}
                onChange={e => setRecipient({ ...recipient, street: e.target.value })}
                placeholder={t('invoices.form.placeholders.street')}
                disabled={isSubmitting}
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="recipientCity">{t('invoices.form.city')}</Label>
                <Input
                  id="recipientCity"
                  value={recipient.city || ''}
                  onChange={e => setRecipient({ ...recipient, city: e.target.value })}
                  placeholder={t('invoices.form.placeholders.city')}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipientPostalCode">{t('invoices.form.postalCode')}</Label>
                <Input
                  id="recipientPostalCode"
                  value={recipient.postalCode || ''}
                  onChange={e => setRecipient({ ...recipient, postalCode: e.target.value })}
                  placeholder={t('invoices.form.placeholders.postalCode')}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipientCountry">{t('invoices.form.country')}</Label>
                <CountrySelect
                  id="recipientCountry"
                  value={recipient.countryCode || ''}
                  onChange={(value) => setRecipient({ ...recipient, countryCode: value })}
                  disabled={isSubmitting}
                  placeholder={t('invoices.form.selectCountry')}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Details */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('invoices.form.payment')}</CardTitle>
              <CardDescription>{t('invoices.form.paymentDesc')}</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="includePayment" className="text-sm font-normal">
                {t('invoices.form.includePaymentDetails')}
              </Label>
              <Switch
                id="includePayment"
                checked={includePaymentDetails}
                onCheckedChange={setIncludePaymentDetails}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </CardHeader>
        {includePaymentDetails ? (
          <CardContent className="space-y-4">
            {/* Bank Account Selector */}
            {bankAccounts.length > 0 && (
              <div className="space-y-2 pb-4 border-b">
                <Label className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {t('invoices.form.selectBankAccount')}
                </Label>
                <Select
                  value={selectedAccountId}
                  onChange={(e) => handleSelectAccount(e.target.value)}
                  disabled={isSubmitting}
                >
                  <option value="">{t('invoices.form.selectBankAccountPlaceholder')}</option>
                  {bankAccounts.map(account => (
                    <option key={account.id} value={String(account.id)}>
                      {account.name} - {account.bankName} {account.isDefault ? `(${t('settings.default')})` : ''}
                    </option>
                  ))}
                  <option value="manual">{t('invoices.form.manualEntry')}</option>
                </Select>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="iban">{t('invoices.form.iban')}</Label>
                <Input
                  id="iban"
                  value={sender.paymentDetails?.iban || ''}
                  onChange={e => updatePaymentDetails('iban', e.target.value)}
                  placeholder={t('invoices.form.placeholders.iban')}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="swift">{t('invoices.form.swift')}</Label>
                <Input
                  id="swift"
                  value={sender.paymentDetails?.swift || ''}
                  onChange={e => updatePaymentDetails('swift', e.target.value)}
                  placeholder={t('invoices.form.placeholders.swift')}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bankName">{t('invoices.form.bankName')}</Label>
                <Input
                  id="bankName"
                  value={sender.paymentDetails?.bankName || ''}
                  onChange={e => updatePaymentDetails('bankName', e.target.value)}
                  placeholder={t('invoices.form.placeholders.bankName')}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accountHolder">{t('invoices.form.accountHolder')}</Label>
                <Input
                  id="accountHolder"
                  value={sender.paymentDetails?.accountHolder || ''}
                  onChange={e => updatePaymentDetails('accountHolder', e.target.value)}
                  placeholder={t('invoices.form.placeholders.accountHolder')}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reference">{t('invoices.form.paymentReference')}</Label>
              <Input
                id="reference"
                value={sender.paymentDetails?.reference || ''}
                onChange={e => updatePaymentDetails('reference', e.target.value)}
                placeholder={t('invoices.form.placeholders.paymentReference')}
                disabled={isSubmitting}
              />
            </div>
          </CardContent>
        ) : (
          <CardContent>
            <p className="text-sm text-muted-foreground">
              {t('invoices.form.paymentNotIncluded')}
            </p>
          </CardContent>
        )}
      </Card>

      {/* Invoice Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('invoices.form.items')}</CardTitle>
              <CardDescription>{t('invoices.form.itemsDesc')}</CardDescription>
            </div>
            <div className="flex gap-2">
              {products.length > 0 && (
                <Button
                  onClick={() => setShowProductDialog(true)}
                  variant="secondary"
                  size="sm"
                  disabled={isSubmitting}
                >
                  <Package className="mr-2 h-4 w-4" />
                  {t('invoices.form.addProduct')}
                </Button>
              )}
              <Button onClick={addItem} variant="outline" size="sm" disabled={isSubmitting}>
                <Plus className="mr-2 h-4 w-4" />
                {t('invoices.form.addLine')}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Header row - hidden on mobile */}
            <div className="hidden lg:grid lg:grid-cols-12 gap-2 text-sm font-medium text-muted-foreground px-1">
              <div className="col-span-4">{t('invoices.form.description')}</div>
              <div className="col-span-1">{t('invoices.form.quantity')}</div>
              <div className="col-span-2">{t('invoices.form.priceWithoutVat')}</div>
              <div className="col-span-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>{t('invoices.form.withholding')} %</div>
                  <div>{t('invoices.form.vat')} %</div>
                </div>
              </div>
              <div className="col-span-2">{t('invoices.form.subtotal')}</div>
              <div className="col-span-1"></div>
            </div>

            {items.map((item) => (
              <div
                key={item.id}
                className="grid gap-2 lg:grid-cols-12 p-4 lg:p-2 rounded-lg border lg:border-0 bg-muted/50 lg:bg-transparent"
              >
                <div className="lg:col-span-4 space-y-1">
                  <Label className="lg:hidden text-xs">{t('invoices.form.description')}</Label>
                  <div className="relative">
                    <Input
                      value={item.description}
                      onChange={e => {
                        updateItem(item.id, 'description', e.target.value)
                        setAutocompleteQuery(e.target.value)
                        setAutocompleteItemId(item.id)
                      }}
                      onFocus={() => {
                        setAutocompleteQuery(item.description)
                        setAutocompleteItemId(item.id)
                      }}
                      onBlur={() => {
                        blurTimeoutRef.current = setTimeout(() => {
                          setAutocompleteItemId(null)
                        }, 150)
                      }}
                      placeholder={t('invoices.form.placeholders.itemDescription')}
                      disabled={isSubmitting}
                      autoComplete="off"
                    />
                    {autocompleteItemId === item.id && searchResults.length > 0 && autocompleteQuery.length >= 1 && (
                      <div className="absolute z-50 top-full left-0 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                        {searchResults.map(product => (
                          <div
                            key={product.id}
                            className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors"
                            onMouseDown={() => {
                              if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
                              updateItem(item.id, 'description', product.description || product.name)
                              updateItem(item.id, 'priceWithoutVat', product.price)
                              updateItem(item.id, 'vatRate', product.vatRate)
                              updateItem(item.id, 'withholdingRate', product.withholdingRate)
                              setAutocompleteItemId(null)
                              setAutocompleteQuery('')
                            }}
                          >
                            <div className="truncate mr-2">
                              <span className="font-medium">{product.name}</span>
                              {product.description && (
                                <span className="text-muted-foreground ml-1">- {product.description}</span>
                              )}
                            </div>
                            <span className="text-muted-foreground whitespace-nowrap">{formatCurrency(product.price)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 lg:col-span-1">
                  <div className="space-y-1">
                    <Label className="lg:hidden text-xs">{t('invoices.form.quantity')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={e => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-1 gap-2 lg:col-span-2">
                  <div className="space-y-1">
                    <Label className="lg:hidden text-xs">{t('invoices.form.priceWithoutVat')}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.priceWithoutVat}
                      onChange={e =>
                        updateItem(item.id, 'priceWithoutVat', parseFloat(e.target.value) || 0)
                      }
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 lg:col-span-2">
                  <div className="space-y-1">
                    <Label className="lg:hidden text-xs">{t('invoices.form.withholding')}</Label>
                    <Select
                      value={String(item.withholdingRate)}
                      onChange={e =>
                        updateItem(item.id, 'withholdingRate', parseInt(e.target.value))
                      }
                      disabled={isSubmitting}
                    >
                      {WITHHOLDING_RATES.map(rate => (
                        <option key={rate.value} value={rate.value}>
                          {rate.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="lg:hidden text-xs">{t('invoices.form.vat')}</Label>
                    <Select
                      value={String(item.vatRate)}
                      onChange={e => updateItem(item.id, 'vatRate', parseInt(e.target.value))}
                      disabled={isSubmitting}
                    >
                      {VAT_RATES.map(rate => (
                        <option key={rate.value} value={rate.value}>
                          {rate.label}
                        </option>
                      ))}
                    </Select>
                    {item.vatRate === 0 && !vatExemptionReason && (
                      <p className="text-xs text-amber-600 flex items-center gap-1 mt-1">
                        <AlertTriangle className="h-3 w-3" />
                        {t('invoices.form.vatExemptionRequired')}
                      </p>
                    )}
                  </div>
                </div>
                <div className="lg:col-span-2 flex items-end">
                  <div className="w-full space-y-1">
                    <Label className="lg:hidden text-xs">{t('invoices.form.subtotal')}</Label>
                    <div className="h-9 px-3 py-2 rounded-md bg-muted text-sm font-medium">
                      {formatCurrency(item.subtotalWithVat)}
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-1 flex items-end justify-end">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(item.id)}
                    disabled={items.length === 1 || isSubmitting}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="mt-6 pt-6 border-t space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('invoices.form.taxBase')}:</span>
              <span className="font-medium">{formatCurrency(totals.baseAmount)}</span>
            </div>
            {totals.totalWithholding > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('invoices.form.withholdings')}:</span>
                <span className="font-medium text-destructive">
                  -{formatCurrency(totals.totalWithholding)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">{t('invoices.form.vat')}:</span>
              <span className="font-medium">{formatCurrency(totals.totalVat)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>{t('common.total')}:</span>
              <span>{formatCurrency(totals.totalAmount)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* VAT Exemption — shown only when 0% VAT item exists */}
      {hasZeroVatItem && (
        <div ref={vatExemptionRef}>
          <Card className={needsExemptionReason ? 'border-destructive ring-1 ring-destructive' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                {t('invoices.form.vatConfig')}
                <span className="text-xs font-normal text-destructive">*</span>
              </CardTitle>
              <CardDescription>{t('invoices.form.vatConfigDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="vatExemption">
                    {t('invoices.form.vatExemption')}
                    <span className="text-destructive ml-1">*</span>
                  </Label>
                  <Select
                    id="vatExemption"
                    value={vatExemptionReason}
                    onChange={e => setVatExemptionReason(e.target.value)}
                    disabled={isSubmitting}
                    className={needsExemptionReason ? 'border-destructive' : ''}
                  >
                    {VAT_EXEMPTION_REASONS.map(reason => (
                      <option key={reason.value} value={reason.value}>
                        {reason.value ? `${reason.article} — ${t(reason.labelKey)}` : t(reason.labelKey)}
                      </option>
                    ))}
                  </Select>
                  {needsExemptionReason && (
                    <p className="text-xs text-destructive">{t('invoices.form.vatExemptionRequired')}</p>
                  )}
                </div>
                {!vatExemptionReason && (
                  <div className="p-3 rounded-lg border border-blue-200 bg-blue-50/50">
                    <div className="flex items-start gap-2.5">
                      <Scale className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                      <p className="text-xs leading-relaxed text-blue-800">
                        {t('invoices.form.vatExemptionHint')}
                      </p>
                    </div>
                  </div>
                )}
                {(() => {
                  const selected = VAT_EXEMPTION_REASONS.find(r => r.value === vatExemptionReason)
                  if (!selected?.descKey) return null
                  return (
                    <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50">
                      <div className="flex items-start gap-2.5">
                        <Scale className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="space-y-1 min-w-0">
                          <p className="text-sm font-semibold text-amber-900">
                            {selected.article}
                          </p>
                          <p className="text-xs leading-relaxed text-amber-800">
                            {t(selected.descKey)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>{t('invoices.form.notes')}</CardTitle>
          <CardDescription>{t('invoices.form.notesDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            id="notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder={t('invoices.form.notesPlaceholder')}
            disabled={isSubmitting}
            rows={3}
          />
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-end">
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard/invoices')}
          disabled={isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button variant="secondary" onClick={handleSaveDraft} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('invoices.form.saving')}
            </>
          ) : (
            t('invoices.form.saveDraft')
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('invoices.form.saving')}
                </>
              ) : (
                <>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('invoices.form.saveAndPreview')}
                  <Globe className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {languages.map(lang => (
              <DropdownMenuItem
                key={lang.value}
                onClick={() => handleSaveAndPreview(lang.value)}
                disabled={isSubmitting}
              >
                <span className="mr-2">{lang.flag}</span>
                {lang.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Product Selection Dialog */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="sm:max-w-[500px] max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('invoices.form.selectProduct')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {products.map(product => (
              <div
                key={product.id}
                className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent cursor-pointer transition-colors"
                onClick={() => addProductToInvoice(product)}
              >
                <div className="space-y-1">
                  <div className="font-medium">{product.name}</div>
                  {product.description && (
                    <div className="text-sm text-muted-foreground line-clamp-1">
                      {product.description}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    IVA: {product.vatRate}%
                    {product.withholdingRate > 0 && ` | Ret: ${product.withholdingRate}%`}
                    {product.unit && ` | ${product.unit}`}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{formatCurrency(product.price)}</div>
                </div>
              </div>
            ))}
            {products.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {t('settings.products.empty')}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
